// + New carrier SectionCard (D3f). Takes the carriers hook as a
// single `m` prop (same pattern as LogisticsRoutesCreateForm w.r.t.
// useLogisticsRoutesPanel) so all state stays owned by the hook.
//
// Hidden until "+ New carrier" is clicked (panel bumps newSignal,
// hook flips creating=true). Renders fallback prompt copy when not
// in create mode so the SectionCard isn't empty.
import { SectionCard } from "@/components/common/SectionCard";
import {
  CARRIER_KIND_LABELS,
  ghostBtnClass,
  inputClass,
  primaryBtnClass,
} from "./logisticsCarriersShared";
import type { useLogisticsCarriersPanel } from "./useLogisticsCarriersPanel";
import type { LogiCarrierKind } from "@/lib/logistics";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

type Props = {
  m: ReturnType<typeof useLogisticsCarriersPanel>;
};

export function LogisticsCarriersCreateForm({ m }: Props) {
  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "logi-carriers-form" }}>
    <SectionCard
      title="Carriers"
      description="Carriers are the haulers we book to move finished units from a plant to a dealer holding lot or end customer. Contracted = standing partner. Freelance = per-job. Dealer BYO = the dealer hauls their own. In-house = our fleet. Carriers picker on the routes board filters to active only -- deactivating here hides a carrier from new routes without breaking historical references."
    >
      {m.creating ? (
        <div className="space-y-2">
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Name — required
            <input
              className={`${inputClass} mt-1`}
              value={m.draft.name}
              onChange={(e) => m.setDraft({ ...m.draft, name: e.target.value })}
              placeholder="e.g. Acme Hauling, Bob's Truck, etc."
            />
          </label>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Kind
            <select
              className={`${inputClass} mt-1`}
              value={m.draft.carrier_kind}
              onChange={(e) =>
                m.setDraft({ ...m.draft, carrier_kind: e.target.value as LogiCarrierKind })
              }
            >
              {m.kindOptions.map((k) => (
                <option key={k} value={k}>
                  {CARRIER_KIND_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Contact name
              <input
                className={`${inputClass} mt-1`}
                value={m.draft.contact_name}
                onChange={(e) => m.setDraft({ ...m.draft, contact_name: e.target.value })}
              />
            </label>
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Contact email
              <input
                className={`${inputClass} mt-1`}
                value={m.draft.contact_email}
                onChange={(e) => m.setDraft({ ...m.draft, contact_email: e.target.value })}
                placeholder="dispatch@example.com"
              />
            </label>
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Contact phone
              <input
                className={`${inputClass} mt-1`}
                value={m.draft.contact_phone}
                onChange={(e) => m.setDraft({ ...m.draft, contact_phone: e.target.value })}
              />
            </label>
          </div>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Coverage areas (comma-separated; e.g. "TX, OK, AR" or "Austin metro, Houston")
            <input
              className={`${inputClass} mt-1`}
              value={m.draft.coverage_areas_text}
              onChange={(e) => m.setDraft({ ...m.draft, coverage_areas_text: e.target.value })}
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Capacity (linear ft)
              <input
                className={`${inputClass} mt-1`}
                value={m.draft.capacity_linear_feet_text}
                onChange={(e) =>
                  m.setDraft({ ...m.draft, capacity_linear_feet_text: e.target.value })
                }
                inputMode="numeric"
                placeholder="e.g. 53"
              />
            </label>
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Insurance expires
              <input
                type="date"
                className={`${inputClass} mt-1`}
                value={m.draft.insurance_expires_at}
                onChange={(e) => m.setDraft({ ...m.draft, insurance_expires_at: e.target.value })}
              />
            </label>
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Contract expires
              <input
                type="date"
                className={`${inputClass} mt-1`}
                value={m.draft.contract_expires_at}
                onChange={(e) => m.setDraft({ ...m.draft, contract_expires_at: e.target.value })}
              />
            </label>
          </div>
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Notes
            <input
              className={`${inputClass} mt-1`}
              value={m.draft.notes}
              onChange={(e) => m.setDraft({ ...m.draft, notes: e.target.value })}
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-zinc-300">
            <input
              type="checkbox"
              checked={m.newIsActive}
              onChange={(e) => m.setNewIsActive(e.target.checked)}
            />
            Active (uncheck to create as inactive)
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              className={primaryBtnClass}
              onClick={() => void m.submitCreate()}
              disabled={m.actionBusy}
            >
              {m.actionBusy ? "Saving…" : "Create carrier"}
            </button>
            <button type="button" className={ghostBtnClass} onClick={m.cancelCreate}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="text-xs text-gray-500 dark:text-zinc-400">
          Use + New carrier above to add a hauler.
        </div>
      )}
    </SectionCard>
    </div>
  );
}
